import type { Song } from "@/lib/music/song";
import type {
  ChordFrequency,
  DifficultyMeasure,
} from "@/lib/music/analyzer-types";
import type { TonalityMode } from "@/lib/music/tonality";
import { Note } from "@/lib/music/note";

export type PieceTonality = {
  tonic: string;
  mode: TonalityMode;
};

export type PieceTessitura = {
  lowest: string;
  highest: string;
};

export type PieceOverviewData = {
  title: string;
  composer: string | null;
  /** Broader work or context (game OST, film, album), already formatted. */
  context: string | null;
  /** `null` only when the analyzer didn't return a key at all. */
  tonality: PieceTonality | null;
  timeSignature: string;
  bpm: number;
  measures: number;
  durationLabel: string;
  noteCount: number;
  notesPerMeasure: number;
  tessitura: PieceTessitura | null;
  /** Top-N chord frequency entries (already sorted desc by the API). */
  chordFrequency: ReadonlyArray<ChordFrequency>;
  /** Optional: the analyzer's representative chord loop, when present. */
  signatureProgression: ReadonlyArray<string> | null;
  difficulty: ReadonlyArray<DifficultyMeasure> | null;
};

/**
 * Pure adapter from a loaded `Song` to the data shape expected by the
 * `PieceOverview` overlay. No React, no side effects, call site can
 * memoize on the `Song` reference.
 *
 * Uses raw note iteration for note count + tessitura so the function
 * doesn't depend on the analyzer being present (a parsed-only Song
 * still produces meaningful stats).
 */
export function getPieceOverviewData(song: Song): PieceOverviewData {
  const timeline = song.getMusicalTimeline();
  const ts = timeline.getTimeSignatureAtTick(0);
  const bpm = Math.round(timeline.getBpmAtTick(0));
  const measures = song.getTotalBars();
  const durationSeconds = timeline.tickToSeconds(song.getDurationTicks());

  const analysis = song.getAnalysis();
  const detectedKey = analysis?.key ?? null;
  const tonality = detectedKey ? parseDetectedKey(detectedKey.label) : null;

  const { noteCount, lowestMidi, highestMidi } = aggregateNotes(song);

  const notesPerMeasure = measures > 0 ? noteCount / measures : 0;

  const tessitura =
    lowestMidi !== null && highestMidi !== null
      ? {
          lowest: Note.fromMidi(lowestMidi).toName(),
          highest: Note.fromMidi(highestMidi).toName(),
        }
      : null;

  const stats = analysis?.statistics ?? null;
  const chordFrequency = stats?.chordFrequency ?? [];
  const rawSignature = stats?.signatureProgression ?? null;
  const signatureProgression =
    rawSignature && rawSignature.length > 0 ? rawSignature : null;

  const difficultyData = song.getDifficulty();
  const difficulty =
    difficultyData && difficultyData.measures && difficultyData.measures.length > 0
      ? difficultyData.measures
      : null;

  return {
    title: song.getTitle() ?? "Sans titre",
    composer: song.getComposer(),
    context: song.getContext(),
    tonality,
    timeSignature: `${ts.numerator}/${ts.denominator}`,
    bpm,
    measures,
    durationLabel: formatDuration(durationSeconds),
    noteCount,
    notesPerMeasure,
    tessitura,
    chordFrequency,
    signatureProgression,
    difficulty,
  };
}

function aggregateNotes(song: Song): {
  noteCount: number;
  lowestMidi: number | null;
  highestMidi: number | null;
} {
  let count = 0;
  let lowest: number | null = null;
  let highest: number | null = null;

  for (const part of song.getParts()) {
    for (const span of part.getRawNotes()) {
      const midi = span.note.toMidi();
      count += 1;

      if (lowest === null || midi < lowest) {
        lowest = midi;
      }

      if (highest === null || midi > highest) {
        highest = midi;
      }
    }
  }

  return { noteCount: count, lowestMidi: lowest, highestMidi: highest };
}

/**
 * Parses an analyzer key label (e.g. `"F major"`, `"C# minor"`) into a
 * tonality. Falls back to null when the label is unexpectedly shaped.
 */
function parseDetectedKey(label: string): PieceTonality | null {
  const parts = label.trim().split(/\s+/);

  if (parts.length < 2) {
    return null;
  }

  const tonic = parts[0];
  const modeWord = parts[1].toLowerCase();
  const mode: TonalityMode | null =
    modeWord === "major" ? "major" : modeWord === "minor" ? "minor" : null;

  if (!mode) {
    return null;
  }

  return { tonic, mode };
}

function formatDuration(totalSeconds: number): string {
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) {
    return ", ";
  }

  const seconds = Math.round(totalSeconds);
  const minutes = Math.floor(seconds / 60);
  const remaining = seconds % 60;
  const padded = remaining.toString().padStart(2, "0");

  if (minutes < 60) {
    return `${minutes}:${padded}`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  const minutesPadded = remainingMinutes.toString().padStart(2, "0");

  return `${hours}:${minutesPadded}:${padded}`;
}
